import * as Font from "expo-font";
import type { ErrorBoundaryProps } from "expo-router";
import { Stack, useNavigationContainerRef } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import * as SystemUI from "expo-system-ui";
import { useEffect } from "react";
import { Platform } from "react-native";
import { useUnistyles } from "react-native-unistyles";

import TRANSLATIONS from "$src/core/constants/translations";
import ConfigProvider from "$src/core/contexts/config";
import { withCore } from "$src/core/contexts/core";
import useAuth from "$src/core/hooks/use-auth";
import useTvosTopShelf from "$src/core/hooks/use-tvos-top-shelf";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import useStorefrontQuery from "$src/core/queries/use-storefront-query";
import Analytics from "$src/core/services/analytics/analytics";
import Logging from "$src/core/services/logging/logging";
import CustomPlatform from "$src/core/utils/platform/platform";
import config from "$src/flavors/hyview/flavor.config";

void SplashScreen.preventAutoHideAsync();

// Before the tree renders: on web this queues the gtag config command ahead of
// any event, so the first screen_view isn't dropped.
if (config.analytics && config.analytics.enabled !== false) {
  Analytics.init(config.analytics);
}

void SystemUI.setBackgroundColorAsync(
  config.theme.colors?.backgroundDefault ?? null,
);

SplashScreen.setOptions({
  duration: 400,
  fade: true,
});

const TvosTopShelfSync = () => {
  const { pushContent } = useTvosTopShelf();
  const { data: storefrontData } = useStorefrontQuery();

  useEffect(() => {
    if (storefrontData) {
      pushContent(storefrontData);
    }
  }, [pushContent, storefrontData]);

  return null;
};

const Layout: React.FC = () => {
  const { theme } = useUnistyles();
  const { status: authStatus } = useAuth();
  const navRef = useNavigationContainerRef();

  /* Bind Sentry routing instrumentation so App Start / screen-load (TTID)
     transactions are produced. No-op in dev and on web. */
  useEffect(() => {
    Logging.registerNavigationContainer?.(navRef);
  }, [navRef]);

  /* Load the fonts (mainly for web, on native/tv the config plugin handles it already) */
  useEffect(() => {
    Font.loadAsync(config.fonts).catch(Logging.error);
  }, []);

  return (
    <>
      <TvosTopShelfSync />

      <Stack
        initialRouteName={authStatus === "unauthenticated" ? "intro" : "(main)"}
        screenOptions={{
          contentStyle: {
            backgroundColor: CustomPlatform.select({
              web: "transparent",
              default: theme.colors.backgroundDefault,
            }),
          },
        }}
      >
        <Stack.Screen name="index" options={{ headerShown: false }} />

        <Stack.Screen
          name="(main)"
          options={{ headerShown: false, animation: "fade" }}
        />

        <Stack.Screen name="intro" options={{ headerShown: false }} />
        <Stack.Screen name="login" options={{ headerShown: false }} />

        <Stack.Protected guard={!Platform.isTV}>
          <Stack.Screen name="register" options={{ headerShown: false }} />
        </Stack.Protected>

        <Stack.Protected guard={authStatus === "authenticated"}>
          <Stack.Screen
            name="profiles/index"
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="profiles/create"
            options={{ headerShown: false }}
          />
          <Stack.Screen name="profiles/[id]" options={{ headerShown: false }} />
        </Stack.Protected>

        {/* Not auth-guarded here: a guarded screen gets no HTML from `expo export`,
            so the host's SPA fallback would serve `/` and break hydration. PairPage
            gates on auth itself and redirects when unauthenticated. */}
        <Stack.Protected guard={Platform.OS === "web"}>
          <Stack.Screen name="pair" options={{ headerShown: false }} />
        </Stack.Protected>
      </Stack>
    </>
  );
};

export const ErrorBoundary = ({ retry, error }: ErrorBoundaryProps) => (
  <ConfigProvider config={config}>
    <ErrorPage
      title={TRANSLATIONS["ERROR/GENERIC/TITLE"]}
      subtitle={TRANSLATIONS["ERROR/GENERIC/SUBTITLE"]}
      ctaText={TRANSLATIONS["COMMON/RETRY"]}
      onPressCta={retry}
      error={error}
    />
  </ConfigProvider>
);

export default withCore(config, Layout);
